/**
 * RichSignatureEditor - a minimal WYSIWYG editor for a rich e-mail signature (B3 rich).
 *
 * DELIBERATELY tiny and dependency-free (no WYSIWYG library): a `contentEditable` region
 * plus a hand-rolled toolbar (Fett, Kursiv, Unterstrichen, Link, Bild, Formatierung
 * entfernen). The repo prefers zero/few dependencies, and a signature needs only bold/
 * italic, a hyperlink and an inline logo - far less than a full editor would drag in.
 *
 * WHAT IS TRUSTED, AND WHAT IS NOT. The HTML this editor emits is NEVER trusted: the api
 * re-sanitises every signature server-side (the strict allowlist) before it is stored or
 * sent - that is the security boundary. Here we do two friendly-but-not-authoritative
 * things: reject an oversized image up front (so the broker learns immediately, not after a
 * silent server drop), and render a client-sanitised "Live-Vorschau" so what they see is
 * roughly what will be stored.
 *
 * IMAGES are inlined as data: URIs (the logo path). Anything over the 256 KB cap is refused
 * at file-pick time with a visible message; the server enforces the same cap as a backstop.
 *
 * [Source: apps/web/src/lib/sanitize-signature-html.ts] (the client mirror of the server allowlist)
 * [Source: apps/api/src/modules/communication/sanitize-signature-html.ts] (the authority)
 */

import { useEffect, useRef, useState } from 'react';
import { Bold, Italic, Underline, Link2, Image as ImageIcon, RemoveFormatting } from 'lucide-react';
import { sanitizeSignatureHtml } from '../../../lib/sanitize-signature-html';

/** Inline-logo cap - kept in lockstep with the server's MAX_INLINE_IMAGE_BYTES (256 KB). */
export const MAX_INLINE_IMAGE_BYTES = 256 * 1024;

interface RichSignatureEditorProps {
  /** Initial HTML (the server's stored bodyHtml, or an escaped legacy plain body). */
  initialHtml: string;
  /** Emits the editor's current HTML on every change. */
  onChange: (html: string) => void;
  ariaLabel?: string;
}

/** Best-effort execCommand - a no-op outside a browser (e.g. jsdom), never throws. */
function exec(command: string, value?: string) {
  try {
    document.execCommand(command, false, value);
  } catch {
    /* contentEditable commands are browser-only; ignore where unavailable. */
  }
}

export function RichSignatureEditor({
  initialHtml,
  onChange,
  ariaLabel = 'Signatur-Editor',
}: RichSignatureEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('https://');
  const [imageError, setImageError] = useState<string | null>(null);

  // Seed the contentEditable ONCE from the server value. It is uncontrolled thereafter (a
  // controlled contentEditable fights the caret on every keystroke), so we push changes OUT
  // via onChange and never re-write innerHTML from props after mount. The parent re-mounts
  // this editor (via `key`) whenever it loads a different signature, so a mount-only seed is
  // correct — `initialHtml` is captured on that mount and never needs to be a dependency.
  const seedRef = useRef(initialHtml);
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = seedRef.current;
      setPreviewHtml(sanitizeSignatureHtml(seedRef.current));
    }
  }, []);

  function emit() {
    const html = editorRef.current?.innerHTML ?? '';
    setPreviewHtml(sanitizeSignatureHtml(html));
    onChange(html);
  }

  function handleFormat(command: string) {
    editorRef.current?.focus();
    exec(command);
    emit();
  }

  function handleInsertLink() {
    const url = linkUrl.trim();
    setLinkOpen(false);
    if (!/^(https?:\/\/|mailto:)/i.test(url)) return; // only safe schemes reach the editor
    editorRef.current?.focus();
    // Insert an anchor at the caret. Its text is the URL when nothing is selected; the server
    // allowlist keeps only the href, so this stays safe regardless of what the browser emits.
    const selection = window.getSelection?.();
    const hasSelection = selection && selection.toString().length > 0;
    if (hasSelection) {
      exec('createLink', url);
    } else {
      const safeText = url.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      exec('insertHTML', `<a href="${url}">${safeText}</a>`);
    }
    setLinkUrl('https://');
    emit();
  }

  function handlePickImage() {
    setImageError(null);
    fileRef.current?.click();
  }

  function handleImageChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setImageError('Nur Bilddateien werden unterstützt.');
      return;
    }
    if (file.size > MAX_INLINE_IMAGE_BYTES) {
      setImageError(
        `Das Bild ist zu gross (max. ${Math.round(MAX_INLINE_IMAGE_BYTES / 1024)} KB).`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      if (!dataUrl.startsWith('data:image/')) return;
      const alt = file.name.replace(/"/g, '');
      editorRef.current?.focus();
      exec('insertHTML', `<img src="${dataUrl}" alt="${alt}" style="max-width:220px" />`);
      emit();
    };
    reader.readAsDataURL(file);
  }

  function handleClearFormatting() {
    if (!editorRef.current) return;
    // "Auf Klartext zurücksetzen": keep the words, drop all markup. A hard reset the broker
    // can always fall back to if a paste brought in formatting they do not want.
    const text = editorRef.current.textContent ?? '';
    editorRef.current.textContent = text;
    emit();
  }

  const toolbarBtn =
    'inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div className="flex flex-col gap-2">
      {/* Toolbar */}
      <div
        className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-muted/40 p-1"
        role="toolbar"
        aria-label="Formatierung"
      >
        <button
          type="button"
          className={toolbarBtn}
          aria-label="Fett"
          onClick={() => handleFormat('bold')}
        >
          <Bold className="size-4" />
        </button>
        <button
          type="button"
          className={toolbarBtn}
          aria-label="Kursiv"
          onClick={() => handleFormat('italic')}
        >
          <Italic className="size-4" />
        </button>
        <button
          type="button"
          className={toolbarBtn}
          aria-label="Unterstrichen"
          onClick={() => handleFormat('underline')}
        >
          <Underline className="size-4" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />
        <button
          type="button"
          className={toolbarBtn}
          aria-label="Link einfügen"
          aria-expanded={linkOpen}
          onClick={() => setLinkOpen((v) => !v)}
        >
          <Link2 className="size-4" />
        </button>
        <button
          type="button"
          className={toolbarBtn}
          aria-label="Bild einfügen"
          onClick={handlePickImage}
        >
          <ImageIcon className="size-4" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />
        <button
          type="button"
          className={toolbarBtn}
          aria-label="Formatierung entfernen"
          onClick={handleClearFormatting}
        >
          <RemoveFormatting className="size-4" />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          aria-hidden="true"
          tabIndex={-1}
          onChange={handleImageChosen}
        />
      </div>

      {/* Inline link entry (revealed by the toolbar Link button). */}
      {linkOpen && (
        <div className="flex items-center gap-2">
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            aria-label="Link-Adresse"
            placeholder="https://…"
            className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <button
            type="button"
            onClick={handleInsertLink}
            className="h-8 rounded-md border border-border px-3 text-sm font-medium hover:bg-muted"
          >
            Einfügen
          </button>
        </div>
      )}

      {imageError !== null && (
        <p className="text-xs text-destructive" role="alert">
          {imageError}
        </p>
      )}

      {/* The editing surface. */}
      <div
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        className="min-h-28 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&_a]:text-primary [&_a]:underline [&_img]:max-w-[220px]"
      />

      {/* Live, client-sanitised preview — a convenience, not the authority. */}
      <div className="rounded-md border border-dashed border-border bg-muted/30 p-3">
        <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Live-Vorschau (bereinigt)
        </p>
        <div
          aria-label="Live-Vorschau der Signatur"
          className="text-sm text-foreground [&_a]:text-primary [&_a]:underline [&_img]:max-w-[220px]"
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      </div>
    </div>
  );
}
